/* ============================================================
   ProfileNudgeModal —— 画像引导 / Gate 双模态弹窗
   ------------------------------------------------------------
   mode='soft' (默认,向后兼容)：
     - HomePage 挂载时自动 fetch /profile/，三字段任一空 → 自动弹
     - 按钮 [稍后] / [去填写]
     - [稍后] 关闭后本会话不再弹（state 标志,刷新页面重置）

   mode='hard'：
     - 外部 open prop 控制（由 useProfileGuard.guardOpen 驱动）
     - 不自动 fetch（fetch + 判定由 hook 完成）
     - 按钮 [取消] / [去填写]；X / ESC / 遮罩等同 [取消]
     - 关闭不算通过 gate —— 调用方靠 status === 'pass' 判定通过

   两种 mode 共用 [去填写] 行为：navigate('/profile/setup')
   ============================================================ */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import Modal from './Modal';
import Button from './Button';
import { missingProfileFields } from '../utils/profile';

export default function ProfileNudgeModal({
  mode = 'soft',
  open: controlledOpen,
  onClose,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [softOpen, setSoftOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isHard = mode === 'hard';

  // soft 模式：挂载时 fetch + 自动弹（原有行为）
  // hard 模式：useEffect 直接 return,fetch + 判定都由 useProfileGuard 做
  useEffect(() => {
    if (isHard) return;
    if (!user || dismissed) return;
    let alive = true;
    client.get('/profile/')
      .then(({ data }) => {
        if (!alive) return;
        const incomplete = missingProfileFields(data).length > 0;
        if (incomplete) setSoftOpen(true);
      })
      .catch(() => { /* 401 / 网络抖动静默；axios 拦截器自处理 token */ });
    return () => { alive = false; };
  }, [user, dismissed, isHard]);

  const open = isHard ? !!controlledOpen : softOpen;

  const handleClose = () => {
    if (isHard) {
      onClose?.();
    } else {
      setSoftOpen(false);
      setDismissed(true);
    }
  };

  const handleGoFill = () => {
    handleClose();
    navigate('/profile/setup');
  };

  const title = isHard ? '完善画像后开始测评' : '完善你的画像';
  const closeLabel = isHard ? '取消' : '稍后';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>{closeLabel}</Button>
          <Button variant="primary" onClick={handleGoFill}>去填写 →</Button>
        </>
      }
    >
      {isHard ? (
        <p style={{ fontSize: 14, lineHeight: 1.75, color: '#26314f', margin: 0 }}>
          AI 测评依赖你的<b>行业、岗位</b>和 <b>AI 使用场景</b>生成题目。
          这三项任一缺失，AI 会用"未填写"代替，
          <b style={{ color: '#1f53e6' }}>题目质量大幅下降</b>。
          完成画像后即可开始测评。
        </p>
      ) : (
        <p style={{ fontSize: 14, lineHeight: 1.75, color: '#26314f', margin: 0 }}>
          AI 测评会根据你的<b>行业、岗位</b>和<b> AI 使用场景</b>出题。
          <b style={{ color: '#1f53e6' }}>未完成信息填写，AI 无法贴近你的场景出题，强烈建议认真填写。</b>
        </p>
      )}
    </Modal>
  );
}
